import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Building2, Lock } from "lucide-react";
import api from "@/lib/api";

const QUICK_ROLES = [
  { email: "mmickey06@gmail.com",         pw: "HCDAdmin2026!",  label: "Michael Mickey · CIO (IT Admin)" },
  { email: "bernaba.icaza@hcdpbc.org",    pw: "HCDGoLive2026!", label: "Bernaba Icaza · General Counsel" },
  { email: "kelly.anderson@hcdpbc.org",   pw: "HCDGoLive2026!", label: "Kelly Anderson · Director of Legal Services" },
  { email: "leyda.benitez@hcdpbc.org",    pw: "HCDGoLive2026!", label: "Leyda Benitez · Attorney" },
  { email: "sarahi.ruiz@hcdpbc.org",      pw: "HCDGoLive2026!", label: "Sarahi Ruiz · Contract Analyst" },
  { email: "anna.delamoa@hcdpbc.org",     pw: "HCDGoLive2026!", label: "Anna Delamoa · Contract Analyst" },
  { email: "cindy.dupont@hcdpbc.org",     pw: "HCDGoLive2026!", label: "Cindy Dupont · Director of IT Applications" },
  { email: "belma.andric@hcdpbc.org",     pw: "HCDGoLive2026!", label: "Belma Andric · EVP · Chief Medical Officer" },
];

export default function LoginPage() {
  const { login, refresh, user, checked } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [ssoStatus, setSsoStatus] = useState(null);

  useEffect(() => {
    if (checked && user) nav(loc.state?.from?.pathname || "/", { replace: true });
  }, [checked, user, nav, loc]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/sso/status"); setSsoStatus(data); } catch {}
    })();
  }, []);

  const startEntra = () => {
    // Real Entra flow — full-page redirect
    const base = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${base}/api/sso/entra/login`;
  };

  const sandboxLogin = async () => {
    if (!email.trim()) { setError("Enter an email to simulate SSO"); return; }
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/sso/entra/sandbox-login", {
        email: email.trim(), display_name: email.split("@")[0], groups: [],
      });
      if (data.access_token) localStorage.setItem("hcd_token", data.access_token);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Sandbox SSO failed");
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(""); setBusy(true);
    try {
      await login(email.trim(), pw);
    } catch (e) {
      setError(e?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const quick = async (r) => {
    setEmail(r.email); setPw(r.pw); setError(""); setBusy(true);
    try { await login(r.email, r.pw); } catch (e) { setError(e?.response?.data?.detail || "Login failed"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#0F172A" }} data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-14 text-white relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md flex items-center justify-center font-bold" style={{ background: "#005289" }}>HCD</div>
            <div>
              <div className="text-lg font-bold leading-tight">Health Care District</div>
              <div className="text-xs text-slate-400">of Palm Beach County</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-lg">
          <h1 className="text-4xl font-bold leading-tight mb-4">Contract Central</h1>
          <p className="text-slate-300 text-base leading-relaxed mb-6">
            The Legal Department's command center for contract routing, review, red-flag capture,
            AI-generated term briefs, and permanent, role-secured contract housing.
          </p>
          <div className="space-y-3 text-sm text-slate-400">
            <div className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "#00AE42" }} />21-day, 40-day and expedited SLA tracking with named per-person day counters</div>
            <div className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "#00AE42" }} />Claude-powered Director's Brief on every uploaded contract</div>
            <div className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "#00AE42" }} />Department-scoped repository with per-folder ACLs</div>
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">© 2026 Health Care District of Palm Beach County</div>
        <div className="absolute inset-0 opacity-10" style={{ background: "radial-gradient(circle at 30% 30%, #005289 0%, transparent 60%)" }} />
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">HCD Legal Department</div>
            <h2 className="text-2xl font-bold text-slate-900">Sign in to Contract Central</h2>
            <p className="text-sm text-slate-500 mt-2">Use your HCDPBC.ORG email to access the system.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-2">Email</label>
              <div className="relative">
                <Building2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded-md text-sm focus:border-[#005289]"
                  placeholder="name@hcdpbc.org"
                  autoComplete="email"
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-2">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-password"
                  type="password"
                  value={pw}
                  onChange={e => setPw(e.target.value)}
                  className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded-md text-sm focus:border-[#005289]"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>
            {error && <div className="text-xs bg-red-50 border border-red-200 rounded-md p-2 text-red-700" data-testid="login-error">{error}</div>}
            <button
              data-testid="login-submit"
              disabled={busy}
              className="w-full h-10 rounded-md text-white font-semibold text-sm disabled:opacity-60"
              style={{ background: "#005289" }}
            >
              {busy ? "Signing in..." : "Sign In"}
            </button>
          </form>

          {/* Microsoft Entra ID */}
          <div className="mt-5" data-testid="sso-block">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 h-px bg-slate-200" />
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">or</div>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            {ssoStatus?.enabled ? (
              <button
                data-testid="sso-entra-btn"
                onClick={startEntra}
                className="w-full h-10 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 text-sm font-semibold flex items-center justify-center gap-2"
              >
                <svg width="16" height="16" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
                Sign in with Microsoft
              </button>
            ) : (
              <div className="space-y-2">
                <button
                  data-testid="sso-sandbox-btn"
                  onClick={sandboxLogin}
                  disabled={busy}
                  className="w-full h-10 rounded-md border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-700 text-sm font-semibold flex items-center justify-center gap-2"
                >
                  <svg width="16" height="16" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
                  Simulate Microsoft SSO (sandbox)
                </button>
                <div className="text-[10px] text-slate-400 text-center" data-testid="sso-sandbox-note">
                  Entra ID not configured — sandbox provisions a user using the email above.
                </div>
              </div>
            )}
          </div>

          <div className="mt-8">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Quick sign-in (demo)</div>
            <div className="space-y-1.5">
              {QUICK_ROLES.map(r => (
                <button
                  key={r.email}
                  onClick={() => quick(r)}
                  disabled={busy}
                  className="w-full text-left px-3 py-2 text-xs border border-slate-200 rounded-md hover:bg-slate-50 hover:border-slate-400"
                  data-testid={`quick-${r.email}`}
                >
                  <div className="font-semibold text-slate-800">{r.label}</div>
                  <div className="text-slate-500 text-[11px]">{r.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
